import React, { FC, useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { usePageTitle } from "../../../../context/navigationContext";
import { BackArrow, Button, ModalVariables, TopProgress } from "../../../../components";
import { PropsContainer } from "../../../../components/table/styles";
import { DropOptions } from "../../../../components";
import { icons } from "../../../../icons";
import {
  Container,
  HeadWrapper,
  RightHeadContainer,
  InstanceName,
} from './styles'
import Overview from './overview'
import { volumeTypesService } from "../../../../api";
import { useUi } from "../../../../context/ui.context";

const VolumeTypePage: FC = () => {
  const navigate = useNavigate()
  const [t] = useTranslation()
  const [options, setOptions] = useState(false)
  const { typeId } = useParams()
  const { viewAlert } = useUi()
  const [volumeType, setVolumeType] = useState<any>()
  usePageTitle(volumeType ? `${t("PAGE_TITLE_VOLUME_TYPE_DETAIL")} - ${volumeType.name || volumeType.id}` : t("PAGE_TITLE_VOLUME_TYPE_DETAIL"))
  const [pageLoading, setPageLoading] = useState<boolean>(true)
  const [viewModal, setViewModal] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(false)
  const [activeAnimation, setActiveAnimation] = useState<boolean>(false)
  const [modalType, setModalType] = useState<any>('')
  const [alertTitle, setAlertTitle] = useState<any>('')
  const [alertSubTitle, setAlertSubTitle] = useState<any>()

  const _updateVolumeType = () => {
    if (typeId) {
      volumeTypesService.getType(typeId)
        .then((res) => {
          setVolumeType(res.data)
          setPageLoading(false)
        })
        .catch((e) => {
          if (e?.response?.data?.detail) {
            viewAlert({
              severity: 'error',
              message: e.response.data?.detail
            })
          }
          setPageLoading(false)
        })
    }
  }

  useEffect(() => {
    _updateVolumeType()
    const interval = setInterval(() => {
      _updateVolumeType()
    }, 5000)
    return () => {
      clearInterval(interval)
    }
  }, [typeId])

  const optionsRef: any = useRef()
  const dropsRef: any = useRef()
  useEffect(() => {
    document.addEventListener('click', handleClickOutside, true);
    return () => {
      document.removeEventListener('click', handleClickOutside, true);
    };
  }, []);

  const handleClickOutside = (event: any) => {
    if (optionsRef && !optionsRef.current.contains(event.target) && !dropsRef.current.contains(event.target)) {
      setOptions(false)
    }
  }

  const items = [
    {
      title: t("EDIT"),
      id: '1',
      icon: icons.Edit,
      onClick: () => {
        navigate(`/openstack/volume-types/${volumeType.id}/edit`)
      },
      divider: false
    },
    {
      title: t("DELETE"),
      id: '2',
      onClick: () => {
        openModal('delete')
        setAlertTitle(t("VOLUME_TYPE"))
        setAlertSubTitle([volumeType])
      },
      icon: icons.DeleteIcon,
      divider: false
    },
  ]

  const openModal = (modal: any) => {
    setModalType(modal)
    setViewModal(true)
    setActiveAnimation(true)
  }

  const closeModal = async (values: any) => {
    let res: any;
    try {
      setLoading(true);
      switch (modalType) {
        case 'delete':
          res = await volumeTypesService.deleteType(volumeType.id);
          if (res?.data?.detail) {
            viewAlert({
              severity: 'info',
              message: res.data?.detail,
            });
          }
          navigate('/openstack/volume-types')
          break;
      }
      setViewModal(false)
      setActiveAnimation(false)
    } catch (e: any) {
      if (e?.response?.data.detail) {
        viewAlert({
          severity: 'error',
          message: e.response.data.detail,
        });
      }
    } finally {
      setLoading(false);
    }
  }

  const closeModalCancel = () => {
    setViewModal(false)
    setActiveAnimation(false)
  }

  if (pageLoading) return <TopProgress loading={pageLoading} />

  return (
    <Container>
      <BackArrow fallbackPath="/openstack/volume-types" />
      <HeadWrapper>
        <InstanceName>
          {volumeType?.name || volumeType?.id}
        </InstanceName>
        <RightHeadContainer>
          <div ref={dropsRef}>
            <Button
              variant="stroke"
              size='small'
              icon={icons.More}
              isFill
              title={t("MENU")}
              onClick={() => setOptions(!options)}
            />
          </div>
          <PropsContainer style={{ top: '35px', right: '0px' }} ref={optionsRef} active={options}>
            <DropOptions
              options={items}
            />
          </PropsContainer>
        </RightHeadContainer>
      </HeadWrapper>
      <Overview volumeType={volumeType} />
      <ModalVariables
        modalType={modalType}
        viewModal={viewModal}
        activeAnimation={activeAnimation}
        closeModal={closeModal}
        closeModalCancel={closeModalCancel}
        alertTitle={alertTitle}
        alertSubTitle={alertSubTitle}
        loading={loading}
      />
    </Container>
  )
}

export default VolumeTypePage
